import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PDFIcon } from 'assets/icons/icon-pdf.svg';
import { ReactComponent as ImageIcon } from 'assets/icons/icon-image.svg';
import { ReactComponent as TextIcon } from 'assets/icons/icon-txt.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close-icon.svg';
import { FileToCompare } from 'store/document-compare';
import { IMAGE_MIME_PREFIX, PDF_MIME_TYPE } from 'constants/fileFormats';
import { RoutePath } from 'router/routes/routePath';

const SFileItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
`;

const SIconWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;

    svg {
        width: 16px;
        height: 16px;
    }
`;

const SFileName = styled.span`
    font-size: ${({ theme }) => theme.font.size.medium};
    font-weight: ${({ theme }) => theme.font.weight.medium};
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const SRemoveButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    flex-shrink: 0;

    &:hover {
        opacity: 0.7;
    }

    svg {
        width: 12px;
        height: 12px;
    }
`;

const SPlaceholder = styled.span`
    font-size: ${({ theme }) => theme.font.size.medium};
    font-weight: ${({ theme }) => theme.font.weight.medium};
    color: ${({ theme }) => theme.colors.bombay};
`;

const SFileWrapper = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    padding: 4px 8px;
    background-color: ${({ theme }) => theme.colors.border_gray};
`;

const icons = [
    {
        mime: PDF_MIME_TYPE,
        icon: PDFIcon,
    },
    {
        mime: IMAGE_MIME_PREFIX,
        icon: ImageIcon,
    },
];

const SLabel = styled.span`
    font-size: ${({ theme }) => theme.font.size.medium};
    font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const getFileIcon = (mimeType?: string) => {
    if (mimeType) {
        const found = icons.find((item) => mimeType.includes(item.mime));
        if (found) {
            return <found.icon />;
        }
    }

    return <TextIcon />;
};

interface Props {
    file: FileToCompare | null;
    label: string;
    onRemove: () => void;
    placeholder?: string;
}

export const CompareFileItem = ({ file, label, onRemove, placeholder }: Props) => {
    const { t } = useTranslation();

    const isOnCompareView = location.pathname.includes(RoutePath.DocumentCompare);

    return (
        <SFileItem>
            <SLabel>{label}</SLabel>
            {file ? (
                <SFileWrapper>
                    <SIconWrapper>{getFileIcon(file.mimeType)}</SIconWrapper>
                    <SFileName title={file.name}>{file.name}</SFileName>
                    {!isOnCompareView && (
                        <SRemoveButton onClick={onRemove} type="button">
                            <CloseIcon />
                        </SRemoveButton>
                    )}
                </SFileWrapper>
            ) : (
                <SPlaceholder>{placeholder || t('compare-mode.not-selected')}</SPlaceholder>
            )}
        </SFileItem>
    );
};
